import { makeStyles, tokens } from '@fluentui/react-components';
import { Flash16Filled, Pause16Filled } from '@fluentui/react-icons';
import { RunStatusBadge } from '../RunsSidebar/RunStatusBadge';
import { MonitoringListItem } from '@/components/MonitoringView/shared/MonitoringListItem';
import type { TriggerHistory } from '@/types/monitoring';
import { formatDateTime, formatDuration, useRelativeTime } from '@/utils/dateTime';

type TriggerHistoryItemProps = {
  history: TriggerHistory;
  isSelected: boolean;
  onSelect: (historyName: string) => void;
};

// Small inline chips that live inside the shared row's meta slot. Distinct
// from RunStatusBadge — they communicate fired/skipped which is orthogonal
// to the trigger run status.
const useStyles = makeStyles({
  firedIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  notFiredIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

export const TriggerHistoryItem = ({
  history,
  isSelected,
  onSelect,
}: TriggerHistoryItemProps) => {
  const styles = useStyles();

  const relativeTime = useRelativeTime(history.startTime);

  const duration =
    history.endTime
      ? formatDuration(
          new Date(history.endTime).getTime() - new Date(history.startTime).getTime(),
          { style: 'execution', fallback: '—' }
        )
      : history.status === 'running'
        ? 'In progress'
        : '—';

  const firedChip = history.fired ? (
    <span className={styles.firedIndicator} title="Trigger fired">
      <Flash16Filled aria-hidden="true" />
      Fired
    </span>
  ) : (
    <span className={styles.notFiredIndicator} title="Trigger evaluated but did not fire">
      <Pause16Filled aria-hidden="true" />
      Skipped
    </span>
  );

  // Match the runs panel meta line: full datetime · fired/skipped · trackingId
  const meta = (
    <>
      {formatDateTime(history.startTime, 'adaptive', { fallback: '' })}
      {' · '}
      {firedChip}
      {history.clientTrackingId ? ` · ${history.clientTrackingId}` : ''}
    </>
  );

  return (
    <MonitoringListItem
      id={history.name}
      status={history.status}
      primaryText={relativeTime}
      badge={<RunStatusBadge status={history.status} size="small" />}
      metaText={meta}
      trailingText={duration}
      isSelected={isSelected}
      onSelect={onSelect}
      tooltipContent={formatDateTime(history.startTime, 'weekday')}
    />
  );
};
